import { NEURAL_CONFIG, AnimationUtils } from "./neural-config"

/**
 * Neural simulation engine for activation propagation and topology changes
 */
export class NeuralSimulation {
  private network: any
  private performance = {
    frameCount: 0,
    lastTopologyUpdate: 0,
  }

  constructor(network: any) {
    this.network = network
  }

  /**
   * Propagate neural signals through the network with slow, dramatic waves
   */
  propagateNeuralSignals(elapsedTime: number): void {
    // Very slow decay for extremely long-lasting, dramatic wave propagation
    for (const neuron of this.network.neurons) {
      if (neuron.activation > 0) {
        neuron.activation *= NEURAL_CONFIG.ACTIVATION_DECAY
        if (neuron.activation < 0.001) {
          neuron.activation = 0
        }
      }
    }

    // Even slower decay for connection trails to create long glowing paths
    for (const neuron of this.network.neurons) {
      for (const connection of neuron.connections) {
        if (connection.activationIntensity > 0) {
          connection.activationIntensity *= 0.998
          if (connection.activationIntensity < 0.001) {
            connection.activationIntensity = 0
          }
        }
      }
    }

    // Much less frequent but more dramatic initiation events
    if (Math.random() < NEURAL_CONFIG.EPICENTER_PROBABILITY) {
      this.triggerEpicenterBurst()
    }

    // Slow, wave-like propagation with momentum building
    this.propagateActivations()

    // Dynamic topology changes based on activation patterns
    this.performance.frameCount++
    if (
      this.performance.frameCount % NEURAL_CONFIG.TOPOLOGY_UPDATE_INTERVAL ===
      0
    ) {
      this.updateNetworkTopology(elapsedTime)
    }
  }

  /**
   * Trigger a dramatic activation burst from an epicenter
   */
  private triggerEpicenterBurst(): void {
    const inputNeurons = this.network.neurons.filter(
      (n: any) => n.layer === 0 || n.type === "input",
    )
    if (inputNeurons.length === 0) return

    const epicenterNeuron =
      inputNeurons[Math.floor(Math.random() * inputNeurons.length)]
    epicenterNeuron.activation = 1.0

    // Create ripple effect around the epicenter
    const rippleRadius = 20
    for (const neuron of this.network.neurons) {
      const distance = epicenterNeuron.position.distanceTo(neuron.position)
      if (distance < rippleRadius && distance > 0) {
        const rippleStrength = 1.0 - distance / rippleRadius
        neuron.activation = Math.max(neuron.activation, rippleStrength * 0.8)
      }
    }
  }

  /**
   * Propagate activations between strongly connected neurons
   */
  private propagateActivations(): void {
    const stronglyActivatedNeurons = this.network.neurons.filter(
      (n: any) => n.activation > 0.3,
    )

    for (const neuron of stronglyActivatedNeurons) {
      for (const connection of neuron.connections) {
        const targetNeuron = this.network.getNeuronById(
          connection.targetNeuronId,
        )
        if (targetNeuron) {
          // Slow but powerful signal propagation
          const propagationDelay = Math.random() * 0.1
          const signalStrength =
            neuron.activation *
            (connection.weight || 0.8) *
            (0.9 - propagationDelay)

          if (signalStrength > 0.2) {
            const newActivation = signalStrength * 0.95
            if (targetNeuron.activation < newActivation) {
              targetNeuron.activation = newActivation

              // Amplify strong signals for dramatic cascading
              if (newActivation > NEURAL_CONFIG.CONNECTION_THRESHOLD) {
                targetNeuron.activation = Math.min(1.0, newActivation * 1.3)
              }
            }

            // Create intense connection activation for dramatic trails
            connection.activationIntensity = Math.max(
              connection.activationIntensity,
              signalStrength * 1.2,
            )
          }
        }
      }
    }
  }

  /**
   * Update network topology based on activity patterns
   */
  private updateNetworkTopology(elapsedTime: number): void {
    // Only update if enough time has passed since last update
    if (elapsedTime - this.performance.lastTopologyUpdate < 1.0) return
    this.performance.lastTopologyUpdate = elapsedTime

    // Cache neuron arrays to avoid repeated filtering
    const highActivityNeurons = this.network.neurons.filter(
      (n: any) => n.activation > 0.8,
    )
    const lowActivityNeurons = this.network.neurons.filter(
      (n: any) => n.activation < 0.05,
    )

    // Create new connections between highly active neurons (Hebbian learning)
    this.createNewConnections(highActivityNeurons, elapsedTime)

    // Remove weak connections from inactive neurons
    this.pruneWeakConnections(lowActivityNeurons)

    // Update connection weights based on activity
    this.updateConnectionWeights()
  }

  /**
   * Create new connections between highly active neurons
   */
  private createNewConnections(
    highActivityNeurons: any[],
    elapsedTime: number,
  ): void {
    if (highActivityNeurons.length >= 2 && Math.random() < 0.02) {
      const sourceNeuron =
        highActivityNeurons[
          Math.floor(Math.random() * highActivityNeurons.length)
        ]
      const targetNeuron =
        highActivityNeurons[
          Math.floor(Math.random() * highActivityNeurons.length)
        ]

      if (sourceNeuron !== targetNeuron) {
        // Check if connection already exists
        const existingConnection = sourceNeuron.connections.find(
          (c: any) => c.targetNeuronId === targetNeuron.id,
        )
        if (!existingConnection) {
          // Create new connection
          const newConnection = {
            targetNeuronId: targetNeuron.id,
            weight: 0.7 + Math.random() * 0.3,
            activationIntensity: 0,
            isNewConnection: true,
            creationTime: elapsedTime,
          }
          sourceNeuron.connections.push(newConnection)
        }
      }
    }
  }

  /**
   * Remove weak connections from inactive neurons
   */
  private pruneWeakConnections(lowActivityNeurons: any[]): void {
    if (lowActivityNeurons.length > 0 && Math.random() < 0.01) {
      for (const neuron of lowActivityNeurons) {
        if (neuron.connections.length > 1) {
          // Remove weakest connection
          const weakestConnection = neuron.connections.reduce(
            (weakest: any, current: any) =>
              current.weight < weakest.weight ? current : weakest,
          )

          if (weakestConnection.weight < 0.3) {
            const index = neuron.connections.indexOf(weakestConnection)
            if (index > -1) {
              neuron.connections.splice(index, 1)
            }
          }
        }
      }
    }
  }

  /**
   * Update connection weights based on activation patterns
   */
  private updateConnectionWeights(): void {
    for (const neuron of this.network.neurons) {
      for (const connection of neuron.connections) {
        if (connection.activationIntensity > 0.5) {
          // Gradually strengthen active connections
          connection.weight = Math.min(1.0, connection.weight * 1.0005)
        } else if (connection.activationIntensity < 0.1) {
          // Gradually weaken inactive connections
          connection.weight = Math.max(0.1, connection.weight * 0.99995)
        }
      }
    }
  }
}
